import React, { useState } from 'react';
import { Layers, Box, List, Plus, MoreVertical, Trash2, Copy, Eye, EyeOff, ChevronRight } from 'lucide-react';
import { useEditorStore } from '../store/useEditorStore';
import ElementPropertiesPanel from './ElementPropertiesPanel';
import { useTranslation } from 'react-i18next';

// 辅助函数：获取元素类型标签
const getElementTypeLabel = (type: string, t: (key: string) => string): string => {
  const typeLabels: Record<string, string> = {
    wall: 'element.type.wall',
    door: 'element.type.door',
    window: 'element.type.window',
    area: 'element.type.area',
    furniture: 'element.type.furniture',
    model3d: 'element.type.model3d',
    circle: 'element.type.circle',
    polygon: 'element.type.polygon',
    stair: 'element.type.stair',
    shape: 'element.type.shape',
    text: 'element.type.text',
  };
  const key = typeLabels[type] || type;
  return t(key);
};

// 辅助函数：获取元素类型的图标
const getElementTypeIcon = (type: string): string => {
  const icons: Record<string, string> = {
    wall: '🧱',
    door: '🚪',
    window: '🪟',
    area: '📦',
    furniture: '🪑',
    model3d: '🏢',
    circle: '⭕',
    polygon: '🔷',
    stair: '🪜',
    shape: '⬡',
    text: '📝',
  };
  return icons[type] || '📌';
};

const RightPanel: React.FC = () => {
  const { t } = useTranslation();
  const [contextMenuElement, setContextMenuElement] = useState<string | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [floorContextMenu, setFloorContextMenu] = useState<{ floorId: string; x: number; y: number } | null>(null);
  
  const { 
    project, 
    currentFloor, 
    currentLayer, 
    rightPanelTab, 
    selectedElements,
    hiddenCategories,
    addLayer, 
    addFloor,
    setRightPanelTab,
    selectElement,
    setCurrentTool,
    setHoveredElement,
    deleteElement,
    copySelectedElements,
    updateElement,
    setCurrentFloor,
    setCurrentLayer,
    setDefaultFloor,
    toggleCategoryVisibility
  } = useEditorStore();

  // 点击外部关闭菜单
  React.useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenuElement) {
        setContextMenuElement(null);
      }
      if (floorContextMenu) {
        setFloorContextMenu(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [contextMenuElement, floorContextMenu]);

  if (!project) return null;

  const floor = project.floors.find((f) => f.id === currentFloor);
  
  // 获取当前图层的元素
  const currentLayerElements = floor?.layers.find((l) => l.id === currentLayer)?.elements || [];
  
  // 统计各类型元素数量
  const elementStats = currentLayerElements.reduce((acc, el) => {
    const type = getElementTypeLabel(el.type, t);
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="w-72 bg-white border-l border-gray-200 flex flex-col">
      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setRightPanelTab('layers')}
          className={`flex-1 px-3 py-2 text-sm font-medium flex items-center justify-center gap-1.5 transition ${
            rightPanelTab === 'layers'
              ? 'text-primary-600 border-b-2 border-primary-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Layers size={14} />
          {t('rightPanel.layers')}
        </button>
        <button
          onClick={() => setRightPanelTab('elements')}
          className={`flex-1 px-3 py-2 text-sm font-medium flex items-center justify-center gap-1.5 transition ${
            rightPanelTab === 'elements'
              ? 'text-primary-600 border-b-2 border-primary-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <List size={14} />
          {t('rightPanel.elements')}
        </button>
        <button
          onClick={() => setRightPanelTab('floors')}
          className={`flex-1 px-3 py-2 text-sm font-medium flex items-center justify-center gap-1.5 transition ${
            rightPanelTab === 'floors'
              ? 'text-primary-600 border-b-2 border-primary-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Box size={14} />
          {t('rightPanel.floors')}
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto scrollbar-thin p-3">
        {rightPanelTab === 'layers' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-gray-900">{t('rightPanel.layerList')}</h3>
              <button
                onClick={() => addLayer(t('rightPanel.newLayer'))}
                className="p-1.5 hover:bg-gray-100 rounded transition"
                title={t('rightPanel.addLayer')}
              >
                <Plus size={16} />
              </button>
            </div>
            {floor?.layers.map((layer) => (
              <div
                key={layer.id}
                onClick={() => setCurrentLayer(layer.id)}
                className={`p-3 rounded-lg mb-2 cursor-pointer transition ${
                  layer.id === currentLayer
                    ? 'bg-primary-50 border border-primary-200 ring-2 ring-primary-100'
                    : 'bg-gray-50 hover:bg-gray-100 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-1">
                    <div
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: layer.color }}
                    />
                    <span className="text-sm font-medium text-gray-900">
                      {/* {layer.name === '__default_layer__' ? t('store.defaultLayer') : layer.name} */}
                      { t('store.defaultLayer') }
                    </span>
                  </div>
                  {layer.id === currentLayer && (
                    <div className="text-primary-600">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {layer.elements.length} {t('rightPanel.elements')}
                </div>
              </div>
            ))}
          </div>
        )}

        {rightPanelTab === 'elements' && (
          <div>
            {/* 显示选中元素的属性 */}
            {selectedElements.length > 0 ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium text-gray-900">{t('rightPanel.elementProps')}</h3>
                  <button
                    onClick={() => {
                      useEditorStore.getState().clearSelection();
                    }}
                    className="text-xs text-gray-500 hover:text-gray-700 transition"
                  >
                    {t('rightPanel.noSelection')}
                  </button>
                </div>
                <ElementPropertiesPanel />
              </>
            ) : (
              <>
                <div className="mb-4">
                  <h3 className="font-medium text-gray-900">{t('rightPanel.elementList')}</h3>
                  {currentLayerElements.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {Object.entries(elementStats).map(([type, count]) => (
                        <span
                          key={type}
                          className="px-2 py-0.5 bg-gray-100 text-xs text-gray-600 rounded"
                        >
                          {type} × {count}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* 按类型分组显示元素 */}
                {Object.entries(
                  currentLayerElements.reduce((groups, element) => {
                    const typeLabel = getElementTypeLabel(element.type, t);
                    if (!groups[typeLabel]) {
                      groups[typeLabel] = [];
                    }
                    groups[typeLabel].push(element);
                    return groups;
                  }, {} as Record<string, typeof currentLayerElements>)
                ).map(([typeLabel, elements]) => {
                  const isCollapsed = collapsedGroups[typeLabel] || false;
                  
                  return (
                    <div key={typeLabel} className="mb-3">
                      {/* 分类标题 */}
                      <div className="flex items-center justify-between px-2 py-1.5 bg-gray-100 rounded hover:bg-gray-200 transition mb-1">
                        <div 
                          className="flex items-center gap-2 flex-1 cursor-pointer"
                          onClick={() => {
                            setCollapsedGroups(prev => ({
                              ...prev,
                              [typeLabel]: !isCollapsed
                            }));
                          }}
                        >
                          <ChevronRight
                            size={14}
                            className={`text-gray-600 transition-transform ${isCollapsed ? '' : 'rotate-90'}`}
                          />
                          <span className="text-sm font-medium text-gray-700">
                            {typeLabel}
                          </span>
                          <span className="text-xs text-gray-500">
                            ({elements.length})
                          </span>
                        </div>
                        <label
                          className="flex items-center gap-2 cursor-pointer group"
                          onClick={(e) => e.stopPropagation()}
                          title={t('rightPanel.onlyDrawMode')}
                        >
                          <span className="text-xs text-gray-600 font-medium">
                            {!hiddenCategories[typeLabel] ? t('rightPanel.show') : t('rightPanel.hide')}
                          </span>
                          <input
                            type="checkbox"
                            checked={!hiddenCategories[typeLabel]}
                            onChange={() => {
                              toggleCategoryVisibility(typeLabel);
                            }}
                            className="sr-only peer"
                          />
                          <div className="relative w-9 h-5 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-500 hover:shadow-md transition-shadow"></div>
                        </label>
                      </div>
                      
                      {/* 元素列表 */}
                      {!isCollapsed && elements.map((element) => {
                    const isSelected = selectedElements.includes(element.id);
                    const showMenu = contextMenuElement === element.id;
                    
                    return (
                      <div
                        key={element.id}
                        className={`relative group rounded-lg mb-2 transition ${
                          isSelected
                            ? 'bg-primary-50 border border-primary-200 ring-2 ring-primary-100'
                            : 'bg-gray-50 hover:bg-gray-100 border border-transparent'
                        }`}
                        onMouseEnter={() => setHoveredElement(element.id)}
                        onMouseLeave={() => setHoveredElement(null)}
                      >
                        <div
                          onClick={() => {
                            selectElement(element.id);
                            setCurrentTool('select' as any);
                          }}
                          className="p-3 cursor-pointer"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-1">
                              <span className="text-lg">{getElementTypeIcon(element.type)}</span>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate">
                                  {element.name}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5">
                                  {getElementTypeLabel(element.type, t)}
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-1">
                              {isSelected && (
                                <div className="text-primary-600">
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              )}
                              
                              {/* 快捷操作按钮 */}
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    selectElement(element.id);
                                    copySelectedElements();
                                  }}
                                  className="p-1 hover:bg-white rounded transition"
                                  title={t('rightPanel.copy')}
                                >
                                  <Copy size={14} className="text-gray-500" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (window.confirm(t('rightPanel.confirmDelete', { name: element.name }))) {
                                      deleteElement(element.id);
                                    }
                                  }}
                                  className="p-1 hover:bg-white rounded transition"
                                  title={t('rightPanel.delete')}
                                >
                                  <Trash2 size={14} className="text-red-500" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setContextMenuElement(showMenu ? null : element.id);
                                  }}
                                  className="p-1 hover:bg-white rounded transition"
                                  title={t('rightPanel.more')}
                                >
                                  <MoreVertical size={14} className="text-gray-500" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* 下拉菜单 */}
                        {showMenu && (
                          <div className="absolute right-2 top-12 z-10 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
                            <button
                              onClick={() => {
                                updateElement(element.id, { visible: !(element as any).visible });
                                setContextMenuElement(null);
                              }}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                            >
                              {(element as any).visible !== false ? (
                                <><EyeOff size={14} /> {t('rightPanel.hide')}</>
                              ) : (
                                <><Eye size={14} /> {t('rightPanel.show')}</>
                              )}
                            </button>
                            <button
                              onClick={() => {
                                selectElement(element.id);
                                copySelectedElements();
                                setContextMenuElement(null);
                              }}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                            >
                              <Copy size={14} /> {t('rightPanel.copy')}
                            </button>
                            <hr className="my-1" />
                            <button
                              onClick={() => {
                                if (window.confirm(t('rightPanel.confirmDelete', { name: element.name }))) {
                                  deleteElement(element.id);
                                  setContextMenuElement(null);
                                }
                              }}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2"
                            >
                              <Trash2 size={14} /> {t('rightPanel.delete')}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                    </div>
                  );
                })}
                
                {currentLayerElements.length === 0 && (
                  <div className="text-center text-gray-400 text-sm py-8">
                    {t('rightPanel.noElements')}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {rightPanelTab === 'floors' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-gray-900">{t('rightPanel.floorList')}</h3>
              <button
                onClick={() => {
                  const maxLevel = Math.max(...project.floors.map((f) => f.level));
                  addFloor(`${maxLevel + 1}F`, maxLevel + 1);
                }}
                className="p-1.5 hover:bg-gray-100 rounded transition"
                title={t('rightPanel.addFloor')}
              >
                <Plus size={16} />
              </button>
            </div>
            {project.floors.map((floorItem) => (
              <div
                key={floorItem.id}
                onClick={() => setCurrentFloor(floorItem.id)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setFloorContextMenu({ floorId: floorItem.id, x: e.clientX, y: e.clientY });
                }}
                className={`p-3 rounded-lg mb-2 cursor-pointer transition relative ${
                  floorItem.id === currentFloor
                    ? 'bg-primary-50 border border-primary-200 ring-2 ring-primary-100'
                    : 'bg-gray-50 hover:bg-gray-100 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">
                        {floorItem.name === '1F' ? t('store.1F') : floorItem.name}
                      </span>
                      {floorItem.isDefault && (
                        <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                          {t('rightPanel.defaultFloor')}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {t('rightPanel.floorHeightLabel')} {floorItem.height}mm | {floorItem.layers.length} {t('rightPanel.floorHeightUnit')}
                    </div>
                  </div>
                  {floorItem.id === currentFloor && (
                    <div className="ml-2 text-primary-600">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 楼层右键菜单 */}
      {floorContextMenu && (
        <div
          className="fixed bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
          style={{ left: floorContextMenu.x, top: floorContextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => {
              setDefaultFloor(floorContextMenu.floorId);
              setFloorContextMenu(null);
            }}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
          >
            <span>⭐</span>
            <span>{t('rightPanel.setDefault')}</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default RightPanel;
